// Página de emissão de NFSe
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../../components/common/Card';
import { Button } from '../../../components/common/Button';
import { Input } from '../../../components/common/Input';
import { Select } from '../../../components/common/Select';
import { Table } from '../../../components/common/Table';
import { Breadcrumbs } from '../../../components/layout/Breadcrumbs';
import { Loading } from '../../../components/common/Loading';
import { Plus, Trash2, AlertCircle } from 'lucide-react';
import { formatCurrency } from '../../../utils/formatters';
import { useEmpresa } from '../../../contexts/EmpresaContext';
import { getClientes } from '../../../services/clientesService';
import { getServicos } from '../../../services/servicosService';
import { createNotaFiscal } from '../../../services/notasFiscaisService';
import { supabase } from '../../../lib/supabase';
import type { Cliente, Servico, ItemNota as ItemNotaType } from '../../../types';

interface ItemServico {
  id: string;
  servico: Servico;
  quantidade: number;
  valorUnitario: number;
  valorTotal: number;
}

export function NFSe() {
  const navigate = useNavigate();
  const { empresaSelecionada, contaId } = useEmpresa();
  const [tomador, setTomador] = useState<Cliente | null>(null);
  const [buscaTomador, setBuscaTomador] = useState('');
  const [itens, setItens] = useState<ItemServico[]>([]);
  const [servicoSelecionado, setServicoSelecionado] = useState('');
  const [quantidade, setQuantidade] = useState(1);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [servicos, setServicos] = useState<Servico[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Carregar clientes e serviços do banco
  useEffect(() => {
    if (!empresaSelecionada) return;

    const carregarDados = async () => {
      setLoading(true);
      try {
        const [clientesData, servicosData] = await Promise.all([
          getClientes(empresaSelecionada.id),
          getServicos(empresaSelecionada.id),
        ]);
        setClientes(clientesData);
        setServicos(servicosData);
      } catch (err: any) {
        console.error('[NFSe] Erro ao carregar dados:', err);
        setError('Erro ao carregar clientes e serviços');
      } finally {
        setLoading(false);
      }
    };

    carregarDados();
  }, [empresaSelecionada]);

  const tomadoresFiltrados = clientes.filter(c =>
    c.nome.toLowerCase().includes(buscaTomador.toLowerCase()) ||
    c.cpf?.includes(buscaTomador) ||
    c.cnpj?.includes(buscaTomador)
  );
  
  const adicionarItem = () => {
    const servico = servicos.find(s => s.id === servicoSelecionado);
    if (!servico) return;
    
    const novoItem: ItemServico = {
      id: Date.now().toString(),
      servico,
      quantidade,
      valorUnitario: servico.valor,
      valorTotal: servico.valor * quantidade,
    };
    
    setItens([...itens, novoItem]);
    setServicoSelecionado('');
    setQuantidade(1);
  };
  
  const removerItem = (id: string) => {
    setItens(itens.filter(i => i.id !== id));
  };
  
  const subtotal = itens.reduce((acc, item) => acc + item.valorTotal, 0);
  const desconto = 0;
  const total = subtotal - desconto;

  // Gerar número e série da nota (simplificado - em produção deve vir do banco)
  const gerarNumeroSerie = () => {
    const numero = Math.floor(Date.now() / 1000).toString().slice(-6);
    const serie = '1'; // Série padrão
    return { numero, serie };
  };

  // Buscar contaId da empresa se não estiver no contexto
  const getContaId = async (): Promise<string> => {
    if (contaId) return contaId;
    
    const { data, error } = await supabase
      .from('empresas')
      .select('conta_id')
      .eq('id', empresaSelecionada!.id)
      .single();
    
    if (error || !data?.conta_id) {
      throw new Error('Erro ao obter conta_id da empresa');
    }
    
    return data.conta_id;
  };

  // Salvar como rascunho
  const handleSalvarRascunho = async () => {
    if (!empresaSelecionada || !tomador || itens.length === 0) {
      setError('Preencha todos os campos obrigatórios');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const contaIdFinal = await getContaId();
      const { numero, serie } = gerarNumeroSerie();

      // Converter itens para formato do banco
      const itensNota: ItemNotaType[] = itens.map(item => ({
        id: item.id,
        tipo: 'servico' as const,
        servicoId: item.servico.id,
        descricao: item.servico.descricao,
        quantidade: item.quantidade,
        unidadeMedida: item.servico.unidadeMedida,
        valorUnitario: item.valorUnitario,
        valorTotal: item.valorTotal,
        ncm: undefined,
        cfop: undefined,
      }));

      // Criar nota fiscal como rascunho
      const nota = await createNotaFiscal(
        {
          tipo: 'NFSe',
          numero,
          serie,
          emitente: empresaSelecionada,
          destinatario: tomador,
          dataEmissao: new Date().toISOString(),
          itens: itensNota,
          subtotal,
          desconto,
          total,
          status: 'rascunho',
        },
        contaIdFinal,
        empresaSelecionada.id
      );

      navigate(`/app/notas/${nota.id}`);
    } catch (err: any) {
      console.error('[NFSe] Erro ao salvar rascunho:', err);
      setError(err.message || 'Erro ao salvar rascunho');
    } finally {
      setSaving(false);
    }
  };

  // Emitir nota
  const handleEmitir = async () => {
    if (!empresaSelecionada || !tomador || itens.length === 0) {
      setError('Preencha todos os campos obrigatórios');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const contaIdFinal = await getContaId();
      const { numero, serie } = gerarNumeroSerie();

      // Converter itens para formato do banco
      const itensNota: ItemNotaType[] = itens.map(item => ({
        id: item.id,
        tipo: 'servico' as const,
        servicoId: item.servico.id,
        descricao: item.servico.descricao,
        quantidade: item.quantidade,
        unidadeMedida: item.servico.unidadeMedida,
        valorUnitario: item.valorUnitario,
        valorTotal: item.valorTotal,
        ncm: undefined,
        cfop: undefined,
      }));

      // Criar nota fiscal como emitida
      const nota = await createNotaFiscal(
        {
          tipo: 'NFSe',
          numero,
          serie,
          emitente: empresaSelecionada,
          destinatario: tomador,
          dataEmissao: new Date().toISOString(),
          itens: itensNota,
          subtotal,
          desconto,
          total,
          status: 'emitida',
        },
        contaIdFinal,
        empresaSelecionada.id
      );

      navigate(`/app/notas/${nota.id}`);
    } catch (err: any) {
      console.error('[NFSe] Erro ao emitir nota:', err);
      setError(err.message || 'Erro ao emitir nota');
    } finally {
      setSaving(false);
    }
  };

  const podeSalvar = empresaSelecionada && tomador && itens.length > 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading />
      </div>
    );
  }

  if (!empresaSelecionada) {
    return (
      <div className="space-y-6">
        <Card>
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-600" />
            <p className="text-gray-600 dark:text-gray-400">
              Selecione uma empresa para emitir notas fiscais
            </p>
          </div>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: 'Emissão', path: '/app/emissao/nfse' }, { label: 'NFSe' }]} />
      
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Emitir NFSe</h1>
      </div>
      
      {/* Dados do Prestador */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Dados do Prestador
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input label="Razão Social" value={empresaSelecionada.razaoSocial} disabled />
          </div>
          <div>
            <Input label="CNPJ" value={empresaSelecionada.cnpj} disabled />
          </div>
          <div>
            <Input label="Email" value={empresaSelecionada.email} disabled />
          </div>
          <div>
            <Input label="Telefone" value={empresaSelecionada.telefone} disabled />
          </div>
        </div>
      </Card>
      
      {/* Dados do Tomador */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Dados do Tomador
        </h2>
        <div className="mb-4">
          <Input
            label="Buscar Tomador"
            placeholder="Nome, CPF ou CNPJ"
            value={buscaTomador}
            onChange={(e) => setBuscaTomador(e.target.value)}
          />
          {buscaTomador && tomadoresFiltrados.length > 0 && (
            <div className="mt-2 border border-gray-200 dark:border-gray-700 rounded-lg max-h-48 overflow-y-auto">
              {tomadoresFiltrados.map((cliente) => (
                <button
                  key={cliente.id}
                  onClick={() => {
                    setTomador(cliente);
                    setBuscaTomador('');
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <p className="font-medium text-gray-900 dark:text-gray-100">{cliente.nome}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {cliente.tipo === 'F' ? cliente.cpf : cliente.cnpj}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
        
        {tomador && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div>
              <Input label="Nome" value={tomador.nome} disabled />
            </div>
            <div>
              <Input 
                label={tomador.tipo === 'F' ? 'CPF' : 'CNPJ'} 
                value={tomador.tipo === 'F' ? tomador.cpf : tomador.cnpj} 
                disabled 
              />
            </div>
            <div>
              <Input label="Email" value={tomador.email} disabled />
            </div>
            <div>
              <Input label="Telefone" value={tomador.telefone} disabled />
            </div>
          </div>
        )}
      </Card>
      
      {/* Serviços */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Serviços Prestados
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <Select
            label="Serviço"
            value={servicoSelecionado}
            onChange={(e) => setServicoSelecionado(e.target.value)}
            options={[
              { value: '', label: 'Selecione um serviço' },
              ...servicos.map(s => ({ value: s.id, label: `${s.codigo} - ${s.descricao}` })),
            ]}
          />
          <Input
            label="Quantidade"
            type="number"
            value={quantidade.toString()}
            onChange={(e) => setQuantidade(Number(e.target.value))}
            min="1"
          />
          <div className="flex items-end">
            <Button onClick={adicionarItem} className="w-full">
              <Plus className="w-4 h-4 mr-2" />
              Adicionar
            </Button>
          </div>
        </div>
        
        {itens.length > 0 && (
          <Table
            data={itens}
            columns={[
              { key: 'servico', label: 'Serviço', render: (item) => item.servico.descricao },
              { key: 'quantidade', label: 'Qtd', render: (item) => item.quantidade },
              { key: 'valorUnitario', label: 'Valor Unit.', render: (item) => formatCurrency(item.valorUnitario) },
              { key: 'valorTotal', label: 'Total', render: (item) => formatCurrency(item.valorTotal) },
              {
                key: 'actions',
                label: 'Ações',
                render: (item) => (
                  <button
                    onClick={() => removerItem(item.id)}
                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                ),
              },
            ]}
          />
        )}
      </Card>
      
      {/* Tributação e Valores */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Tributação
          </h2>
          <div className="space-y-4">
            <Select
              label="Regime Tributário"
              options={[
                { value: 'simples', label: 'Simples Nacional' },
                { value: 'presumido', label: 'Lucro Presumido' },
                { value: 'real', label: 'Lucro Real' },
              ]}
            />
            <Input label="Código Municipal" placeholder="Ex: 1.03.01" />
            <Input label="Alíquota (%)" type="number" placeholder="0.00" />
          </div>
        </Card>
        
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Valores
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Desconto:</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(desconto)}</span>
            </div>
            <div className="flex justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
              <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">Total:</span>
              <span className="text-lg font-bold text-primary-600 dark:text-primary-400">{formatCurrency(total)}</span>
            </div>
          </div>
        </Card>
      </div>
      
      {/* Mensagem de erro */}
      {error && (
        <Card>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </Card>
      )}

      {/* Ações */}
      <div className="flex gap-4 justify-end">
        <Button 
          variant="secondary" 
          onClick={handleSalvarRascunho}
          disabled={!podeSalvar || saving}
          isLoading={saving}
        >
          Salvar Rascunho
        </Button>
        <Button 
          variant="primary" 
          onClick={handleEmitir}
          disabled={!podeSalvar || saving}
          isLoading={saving}
        >
          Emitir Nota
        </Button>
      </div>
    </div>
  );
}
